import axios from "axios";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import {
  CHAT_CACHE_BUSTER,
  CHAT_CACHE_MAX_AGE_MS,
  createQueryCachePersister,
  getRetryDelay,
  shouldRetryRequest,
} from '@photon/chat-core';
import { apiUrl } from '../config';
import { STALE_48H, GC_7DAYS } from '../constants/const';
import { setupBroadcastSync } from './broadcastSync';
import { expireLocalSession } from '../services/SessionExpiry';
import { registerAuthSessionInvalidationHandler } from '../services/AuthSession';
import logger from '../services/Logger';
import indexedDbChatCacheAdapter from './indexedDbChatCacheAdapter';

export const baseURL = apiUrl;

/**
 * Thrown by the response interceptor when the server returns 401 or 403.
 * Catch blocks should check `err instanceof AuthError` and return silently —
 * the auth middleware handles the redirect.
 */
export class AuthError extends Error {
  constructor(originalError) {
    super("Auth error");
    this.name = "AuthError";
    this.response = originalError?.response;
    this.status = originalError?.response?.status;
  }
}

export const handleAuthResponseError = (error) => {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return error;

  if (status === 401) {
    const rejectedToken = error?.config?.headers?.['X-Session-Token'];
    if (rejectedToken) {
      expireLocalSession(rejectedToken, {
        reason: 'api_401',
        source: 'axios_interceptor',
      });
    }
  }
  return new AuthError(error);
};

const persister = createQueryCachePersister({
  storage: indexedDbChatCacheAdapter,
  onPersistError: (error) => {
    logger.error('ChatCache', 'Failed to persist query cache', {
      error: error?.message,
    });
  },
});

const api = (config) => {
  const axiosInstance = axios.create({
    baseURL: baseURL,
  });

  // Add session token to all requests
  axiosInstance.interceptors.request.use(
    (config) => {
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        config.headers['X-Session-Token'] = sessionToken;
      }
      // TODO: Add Cloud Run identity token when available
      // config.headers['Authorization'] = `Bearer ${cloudRunToken}`;
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // A 401 invalidates the exact rejected token. A 403 is authorization-only
  // and must leave the authenticated session intact.
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      return Promise.reject(handleAuthResponseError(error));
    }
  );

  return axiosInstance(config);
};

// Create QueryClient ONCE, outside the component to persist cache across renders
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache configuration for better performance
      staleTime: STALE_48H,  // 48 hours - listeners handle real-time updates (persisted cache)
      gcTime: GC_7DAYS,       // 7 days - keep in cache for persistence
      refetchOnWindowFocus: false,      // Don't refetch on window focus
      refetchOnReconnect: true,         // Refetch when reconnecting
      // Never retry 4xx errors — the request is bad and won't fix itself (e.g. expired session token).
      // Only retry on network errors or 5xx responses.
      retry: (failureCount, error) => shouldRetryRequest(failureCount, error, 2),
      retryDelay: getRetryDelay,
    },
    mutations: {
      retry: (failureCount, error) => shouldRetryRequest(failureCount, error, 1),
    },
  },
});

export const clearChatCache = async () => {
  queryClient.clear();
  try {
    await persister.removeClient();
  } catch (error) {
    logger.error('ChatCache', 'Failed to remove persisted query cache', {
      error: error?.message,
    });
  }
};

registerAuthSessionInvalidationHandler(clearChatCache);

// Sync query invalidations across browser tabs
setupBroadcastSync(queryClient);

export const ApiProvider = ({ children }) => {
  return (
    <PersistQueryClientProvider 
      client={queryClient} 
      persistOptions={{ 
        persister,
        maxAge: CHAT_CACHE_MAX_AGE_MS,
        buster: CHAT_CACHE_BUSTER,
        dehydrateOptions: {
          shouldDehydrateMutation: () => false, // Don't persist mutations (they contain non-serializable axios objects)
          shouldDehydrateQuery: (query) => {
            // Don't persist queries that might contain non-serializable data
            // Only persist if the query has successfully fetched data
            return query.state.status === 'success' && query.state.data !== undefined;
          },
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
};

export default api;
